import { useEffect, useState, useCallback, useRef } from "react";
import ReactDOM from "react-dom";
import { useTutorial } from "../../context/tutorialContext";
import TutorialTooltip from "./TutorialTooltip";
import TutorialWelcome from "./TutorialWelcome";
import "./Tutorial.css";

function isElementVisible(el) {
    if (!el) return false;
    const rect = el.getBoundingClientRect();
    if (rect.width === 0 && rect.height === 0) return false;
    const style = window.getComputedStyle(el);
    if (style.display === "none" || style.visibility === "hidden" || style.opacity === "0") return false;
    return true;
}

function TutorialOverlay() {
    const { active, currentStep, next } = useTutorial();
    const [targetRect, setTargetRect] = useState(null);
    const [ready, setReady] = useState(false);
    const rafRef = useRef(null);
    const attemptsRef = useRef(0);
    const skippingRef = useRef(false); 

    const measureTarget = useCallback((el, padding) => {
        const rect = el.getBoundingClientRect();
        setTargetRect({
            top: rect.top - padding,
            left: rect.left - padding,
            width: rect.width + padding * 2,
            height: rect.height + padding * 2,
        });
    }, []);

    const findAndPositionTarget = useCallback(() => {
        if (!currentStep || !currentStep.target) {
            setTargetRect(null);
            setReady(true);
            return;
        }

        const el = document.querySelector(currentStep.target);
        if (el && isElementVisible(el)) {
            const padding = currentStep.spotlightPadding || 8;
            el.scrollIntoView({ behavior: "instant", block: "center" });
            measureTarget(el, padding);
            setReady(true);
            attemptsRef.current = 0;
        } else {
            attemptsRef.current += 1;
            if (attemptsRef.current > 60) {
                attemptsRef.current = 0;
                if (!skippingRef.current) {
                    skippingRef.current = true;
                    next();
                }
                return;
            } else {
                rafRef.current = requestAnimationFrame(findAndPositionTarget);
            }
        }
    }, [currentStep, next, measureTarget]);

    useEffect(() => {
        if (!active || !currentStep) {
            setReady(false);
            setTargetRect(null);
            return;
        }

        setReady(false);
        attemptsRef.current = 0;
        skippingRef.current = false;

        const run = async () => {
            if (currentStep.onBeforeShow) {
                try {
                    await currentStep.onBeforeShow();
                } catch {}
            }
            rafRef.current = requestAnimationFrame(findAndPositionTarget);
        };
        run();

        return () => {
            if (rafRef.current) cancelAnimationFrame(rafRef.current);
        };
    }, [active, currentStep, findAndPositionTarget]);

    useEffect(() => {
        if (!active || !targetRect || !currentStep?.target) return;

        const padding = currentStep.spotlightPadding || 8;

        const updatePosition = () => {
            const el = document.querySelector(currentStep.target);
            if (!el) return;
            measureTarget(el, padding);
        };

        const observer = new ResizeObserver(updatePosition);
        const el = document.querySelector(currentStep.target);
        if (el) observer.observe(el);

        window.addEventListener("scroll", updatePosition, true);

        return () => {
            observer.disconnect();
            window.removeEventListener("scroll", updatePosition, true);
        };
    }, [active, targetRect, currentStep, measureTarget]);

    if (!active || !currentStep) return null;

    const isModal = currentStep.isModal;
    const isInsideModal = currentStep.insideModal;

    const overlayClass = `ui-tutorial-overlay${isInsideModal ? " ui-tutorial-overlay--above-modal" : ""}`;

    const overlay = (
        <div className={overlayClass}>
            {!isModal && (
                <svg className="ui-tutorial-backdrop" width="100%" height="100%">
                    <defs>
                        <mask id="tutorial-spotlight-mask">
                            <rect x="0" y="0" width="100%" height="100%" fill="white" />
                            {targetRect && ready && (
                                <rect
                                    x={targetRect.left}
                                    y={targetRect.top}
                                    width={targetRect.width}
                                    height={targetRect.height}
                                    rx="8"
                                    fill="black"
                                />
                            )}
                        </mask>
                    </defs>
                    <rect
                        x="0"
                        y="0"
                        width="100%"
                        height="100%"
                        fill="rgba(0,0,0,0.6)"
                        mask="url(#tutorial-spotlight-mask)"
                    />
                </svg>
            )}

            {isModal && <div className="ui-tutorial-modal-backdrop" />}

            {!ready && !isModal && (
                <div className="ui-tutorial-loading">
                    <div className="ui-tutorial-loading__spinner" />
                    <span className="ui-tutorial-loading__text">Loading...</span>
                </div>
            )}

            {ready && !isModal && (
                <TutorialTooltip targetRect={targetRect} />
            )}

            {ready && isModal && (
                <TutorialWelcome />
            )}
        </div>
    );

    return ReactDOM.createPortal(overlay, document.body);
}

export default TutorialOverlay;
